import { get, some } from 'lodash'

import { getAccessToken, getOrganizationId, getSpaceId, getTenantAPIHost } from '@/store/contextSubscriber'
import { gunzip } from '@/utilities/compression'
import ServiceURL from '@/utilities/ServiceURL'

export const replacePlaceholders = (object: any) => {
  const copy = { ...object }

  Object.keys(copy).forEach(key => {
    if (copy[key] === ':spaceId') {
      copy[key] = getSpaceId()
    } else if (copy[key] === ':organizationId') {
      copy[key] = getOrganizationId()
    }
  })

  return copy
}

export const statusCodeMap = {
  400: { message: 'Bad Request' },
  401: { message: 'Unauthorized' },
  403: { message: 'Forbidden' },
  404: { message: 'Not Found' },
  413: { message: 'Payload Too Big' },
  500: { message: 'Internal Server Error' },
  502: { message: 'Bad Gateway' },
  504: { message: 'Gateway Timeout' }
}

/**
 * Makes an API request using `fetch` to the specified Workgrid service. Provides niceties for common tasks like
 * specifying the current organization or space ID in the URL, encoding of URL query string parameters, and applying the
 * current JWT authorization token.
 *
 * @param {string} url - URL of the API request to fetch. Placeholders `:organizationId` and `:spaceId` can be used.
 * @param {object} [params] - Map of parameters to pass to fetch for the API request.
 * @param {object} [params.body] - Body of the request for applicable HTTP methods. Automatically serialized for JSON content typed requests. Placeholders `:organizationId` and `:spaceId` can be used.
 * @param {object} [params.headers] - Map of HTTP headers for the request. Placeholders `:organizationId` and `:spaceId` can be used.
 * @param {string} [params.method=GET] - HTTP method of the request.
 * @param {object} [params.search] - Map of query string parameters to append to the URL. Automatically encoded and separated. Placeholders `:organizationId` and `:spaceId` can be used.
 * @param {object} [options] - Map of configuration options to use for the API request.
 * @param {?boolean|string} [options.service=tenant] - Name of the Workgrid service to make the API request to.
 * @param {boolean} [options.setAuthorization=true] - Whether the authorization header should be automatically set with the current JWT token.
 * @throws {Error} When an authorization error or JSend error is returned.
 */
export default async (url: any, params?: any, options?: any) => {
  const headers = replacePlaceholders(
    Object.assign({}, { 'Content-Type': 'application/json' }, get(params, 'headers', {}))
  )

  params = Object.assign({}, { method: 'GET' }, params, { headers })
  options = Object.assign({}, { service: 'tenant', setAuthorization: true }, options)

  if (options.service !== false) {
    const tenantAPIHost = getTenantAPIHost()

    if (tenantAPIHost) {
      const serviceUrl = ServiceURL(tenantAPIHost)

      // @ts-expect-error
      if (serviceUrl[options.service]) {
        // @ts-expect-error
        url = `${serviceUrl[options.service]}${url}`
      }
    }
  }

  url = url.replace(':organizationId', getOrganizationId()).replace(':spaceId', getSpaceId())

  if (params.search) {
    url = `${url}${url.endsWith('?') ? '' : '?'}${buildSearchParams(replacePlaceholders(params.search))}`
    delete params.search
  }

  if (options.setAuthorization) {
    params.credentials = 'include'
    params.headers.Authorization = `Bearer ${getAccessToken()}`
  }

  if (params.body && isJSON(headers) && headers['x-workgrid-content-encoding'] !== 'workgrid-gzip') {
    params.body = JSON.stringify(replacePlaceholders(params.body))
  }

  // gunzip the body, and remove the encoding headers if gzip is disabled
  if (process.env.REACT_APP_USE_WORKGRID_GZIP === 'false') {
    delete params.headers['x-workgrid-accept-encoding']
    if (params.headers['x-workgrid-content-encoding'] === 'workgrid-gzip') {
      delete params.headers['x-workgrid-content-encoding']
      params.body = JSON.stringify(gunzip(params.body))
    }
  }

  const response = await fetch(url, params)

  // @ts-expect-error
  if (statusCodeMap[response.status]) {
    // @ts-expect-error
    throwCodedError(statusCodeMap[response.status].message, response.status)
  }

  // @ts-expect-error
  if (!response.headers.get('Content-Type').startsWith('application/json')) {
    // @ts-expect-error
    if (response.headers.get('Content-Type').startsWith('text/csv')) {
      return response.blob()
    }
    return response.text()
  }

  let jsonResponse

  if (response.headers.get('x-workgrid-content-encoding') === 'workgrid-gzip') {
    const text = await response.text()

    jsonResponse = gunzip(text)
  }

  if (!jsonResponse) {
    jsonResponse = await response.json()
  }

  //if jsend response format has an unsuccesful status
  if (jsonResponse.data && jsonResponse.status && jsonResponse.status !== 'success') {
    throwCodedError(
      get(jsonResponse, 'message', 'Request failed'),
      response.status,
      jsonResponse.code,
      jsonResponse.data
    )
  }

  return get(jsonResponse, 'data', jsonResponse)
}

const buildSearchParams = (params: any) => {
  return Object.keys(params)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&')
}

const isJSON = (headers: any) => {
  return some(
    headers,
    (value, key) => key.toLowerCase() === 'content-type' && value.toLowerCase() === 'application/json'
  )
}

const throwCodedError = (message: any, status: any, code: any, data: any) => {
  const error = new Error(message) as any
  error.code = code || status
  error.data = data
  error.status = status

  throw error
}
